import { useEffect, useState } from 'react';
import { Link, Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { fmtDay } from '../../shared/Panel';
import { plural } from '../../shared/plural';
import { useCompanies } from '../../shared/companies';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import {
  fetchFleetPlan,
  fleetWithoutTmsPlan,
  type FleetPlan,
  type FleetPlanFilters,
  type FleetPlanRow,
} from './vehiclePlanApi';
import { PlanLegend, PlanTimeline, type TimelineColumn, type TimelineGap } from './PlanTimeline';
import { driverWeekOn, driverWeekText, driverWeekTitle, isReplicatedWeek } from './driverWeeks';
import { shiftDayKey, todayKey } from './timeline';
import { OnlyMineToggle, useOnlyMine } from './myVehicles';
import { vehicleTypeLabel } from './vehicleTypes';
import { hasSegments } from './planSegments';
import { ArchivedToggle } from './archivedVehicles';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FLEET_HIDE_ARCHIVED,
  FLEET_MATCHING_ORDERS,
  FLEET_SCHEDULE_MOCKUP,
  OCCUPIED_UNTIL_TMS_PLAN,
} from './fleetFeatures';
import {
  DEFAULT_FLEET_VIEW,
  FLEET_SCALES,
  FLEET_STATE_SHOW_LABELS,
  FLEET_STATE_SHOW_VALUES,
  fleetDayMinWidth,
  fleetLabelWidth,
  fleetHomeStart,
  fleetStep,
  fleetViewTo,
  gapLabel,
  gapTitle,
  fleetViewFromAddress,
  parseFleetShow,
  readFleetView,
  scaleLabel,
  writeFleetView,
  type FleetScale,
  type FleetShow,
  type FleetViewState,
} from './fleetScheduleRules';
import { hashQueryParams } from '../../shared/hashQuery';
import { FEATURE_PLANNER_DASHBOARD } from '../ftlDashboards/ftlDashboardsApi';
import { useNarrowLayout } from '../useNarrowLayout';
import {
  FleetCountersBar,
  FleetMatchesDialog,
  MatchingCell,
  ReleaseCell,
  VehicleMarkers,
  matchingNote,
} from './FleetMockupParts';

/**
 * Harmonogram floty — auta w wierszach, dni w kolumnach, tylko odczyt.
 *
 * Status z 29.09.2026: planiści pracują **z listy aut, nie z listy zleceń**,
 * a obsadzenie ma być widoczne w planie pracy floty. Wiersz to auto pod
 * zlecenie (bez naczep i wózków), pasek — obsadzenie albo wariant tego auta.
 * Kliknięcie numeru auta otwiera jego kartę, kliknięcie paska — zlecenie.
 *
 * „Opiekun FTL” to pole `ftl_caretaker` z kartoteki TMS — opiekun auta po
 * stronie FTL, nie kierowca. Tu jest zwykłym filtrem widoku, nie
 * uprawnieniem: każdy widzi całą flotę. Przełącznik „Tylko moje pojazdy”
 * ustawia ten filtr na opiekuna wskazanego w menu użytkownika
 * (`myVehicles.tsx`); bez wskazania niczego nie zawęża.
 *
 * Wiersz auta ma trzy cienkie linie (omówienie 01.10.2026): zrealizowane,
 * w realizacji, zaplanowane — z oknem zlecenia linią przerywaną i szacunkiem
 * wykonania pełnym paskiem, jak na karcie auta (`PlanTimeline.tsx`). Od
 * 06.10.2026 pasek jest rozpisany na odcinki (dolot, załadunek, przejazd,
 * przerwy i odpoczynek, rozładunek), a linie są wyższe; szerokość dni bez
 * zmian — przy 1366 px oś mieści się bez przewijania w bok.
 *
 * Pod numerem auta — kierowca z harmonogramu kierowców na bieżący tydzień
 * (kursywą, gdy tydzień jest powielony z wcześniejszego miesiąca); źródło
 * i status w dymku.
 *
 * Domyślnie bez aut archiwalnych (07.10.2026, prezentacja 02.10 pkt 14) —
 * ta sama reguła co lista floty, liczona w API; przełącznik „Pokaż także
 * archiwalne” je przywraca i nie jest zapamiętywany.
 *
 * ## Układ według makiety FTL (15.10.2026, Etap 1)
 *
 * Za przełącznikiem `fleetScheduleMockup` (makieta p. Mariusza z 13.10,
 * pkt 9.1; Etap 1 — decyzja Bartka 15.10.2026; reguły w API
 * `vehicle-schedules/fleet-mockup.ts`, tu `fleetScheduleRules.ts`):
 * - skala 1 / 3 / 7 / 14 dni (domyślnie 3), strzałki o dzień albo tydzień;
 * - kolumna „Wolny od · gdzie” (po ostatnim obsadzeniu, reguła pozycji
 *   z rekomendacji) i kolejność według czasu zwolnienia;
 * - „Pokaż: z alertem / z wariantem” obok dotychczasowych filtrów;
 * - żetony przy aucie (alert, bez kierowcy w kartotece);
 * - odcinek spóźnienia za oknem rozładunku i luki w planie na osi;
 * - liczniki wolnych pojazdów teraz / za 6 / 12 / 24 h nad osią (bez
 *   bilansu regionu — to Etap 2);
 * - filtry i skala zapamiętane na czas karty przeglądarki — po powrocie
 *   z karty pojazdu widok jest ten sam (wtedy także „Pokaż także
 *   archiwalne”).
 * Z `fleetMatchingOrders` dochodzi kolumna „Pasujące” i liczby w lukach
 * (liczone w tle), a kliknięcie luki otwiera listę zleceń z załadunkiem
 * w tej luce; bez niego luka prowadzi do karty pojazdu.
 * Wyłączony przełącznik — ekran dokładnie jak dotąd.
 */

/** Widok: wczoraj i dwa tygodnie naprzód — tak jak domyślne okno API. */
const DAYS_BACK = 1;
const VIEW_DAYS = 15;
const STEP_DAYS = 7;
const SEARCH_DELAY_MS = 400;

/** Typy aut pod zlecenie w kartotece klienta (naczepy i wózki nie stają pod zlecenie). */
const VEHICLE_TYPES = ['JUMBO TRUCK', 'MEGA TRUCK', 'SOLO', 'OTHER'];

const VehicleLabel = ({ v, today, markers = false }: { v: FleetPlanRow; today: string; markers?: boolean }) => {
  const week = driverWeekOn(v.driverWeeks, today);
  const details = [vehicleTypeLabel(v.carType), v.company?.name, v.ftlCaretaker ? `opiekun: ${v.ftlCaretaker}` : null]
    .filter(Boolean)
    .join(' · ');
  // Kartoteka klienta mówi wprost, że auto nie jeździ — pokazujemy to przy
  // numerze, bo pusty wiersz wyglądałby jak auto wolne. Bez powodu z TMS —
  // te same słowa co na liście pojazdów: „poza eksploatacją” (pole nie mówi
  // o usterce, `cells.tsx`) albo „niedostępny” (poprawa brzmienia 01.10.2026).
  const unavailable = v.available === false || v.operational === false;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
        <Link to={`/cars/${v.id}/show`}>{v.registrationNumber ?? v.name ?? `#${v.id}`}</Link>
        {unavailable ? (
          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
            {v.unavailableStatus ?? (v.operational === false ? 'poza eksploatacją' : 'niedostępny')}
          </Typography>
        ) : null}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }} title={details}>
        {week ? (
          <Box
            component="span"
            title={driverWeekTitle(week)}
            data-testid={`fleet-driver-${v.id}`}
            sx={isReplicatedWeek(week) ? { fontStyle: 'italic' } : { color: 'text.primary' }}
          >
            {driverWeekText(week)}
            {details ? ' · ' : ''}
          </Box>
        ) : null}
        {details || (week ? '' : '—')}
      </Typography>
      {markers ? <VehicleMarkers v={v} /> : null}
    </Box>
  );
};

/** Ponowny odczyt, dopóki liczba pasujących zleceń się liczy (ms, najwyżej razy). */
const MATCHING_POLL_MS = 15_000;
const MATCHING_POLL_LIMIT = 8;

export const FleetSchedule = () => {
  const companies = useCompanies();
  // Układ według makiety FTL, Etap 1 (15.10.2026) — patrz opis wyżej.
  const mockup = useFeature(FLEET_SCHEDULE_MOCKUP);
  // Wąskie okno (poniżej 1280 px, 29.10.2026): węższa kolumna pojazdu i dni przy 14 dniach.
  const narrow = useNarrowLayout();
  const matchingOn = useFeature(FLEET_MATCHING_ORDERS) && mockup;
  // Zapamiętany widok tylko w układzie wg makiety; bez niego — jak dotąd.
  // Kafelek pulpitu planisty (20.10.2026) otwiera harmonogram z filtrem
  // w adresie — wtedy widok z adresu, inaczej zapamiętany.
  const [saved] = useState<FleetViewState>(() => {
    if (!mockup) return DEFAULT_FLEET_VIEW;
    const remembered = readFleetView();
    return fleetViewFromAddress(hashQueryParams(), remembered) ?? remembered;
  });
  // „Pokaż: wolne teraz / zajęte teraz / bez pozycji” — pulpit planisty.
  const stateShowOn = useFeature(FEATURE_PLANNER_DASHBOARD);
  const [days, setDays] = useState<FleetScale>(saved.days);
  const [fromKey, setFromKey] = useState<string>(() =>
    mockup ? (saved.fromKey ?? fleetHomeStart(todayKey(), saved.days)) : shiftDayKey(todayKey(), -DAYS_BACK),
  );
  const [companyId, setCompanyId] = useState(saved.companyId);
  const [caretaker, setCaretaker] = useState(saved.caretaker);
  const [carType, setCarType] = useState(saved.carType);
  const [busy, setBusy] = useState<'' | 'assigned' | 'free'>(saved.busy);
  const [show, setShow] = useState<FleetShow>(saved.show);
  const [includeArchived, setIncludeArchived] = useState(saved.includeArchived);
  // „Pokaż także archiwalne” tylko z przełącznikiem `fleetHideArchived`
  // (12.10.2026); bez niego API oddaje całą flotę, jak w 0.31.3.
  const hideArchived = useFeature(FLEET_HIDE_ARCHIVED);
  const [search, setSearch] = useState(saved.search);
  const [q, setQ] = useState(saved.search.trim());
  const [reloadTick, setReloadTick] = useState(0);
  const [polls, setPolls] = useState(0);
  const [matchesFor, setMatchesFor] = useState<{
    carId: number;
    label: string;
    from?: string;
    until?: string | null;
  } | null>(null);
  const [loadedPlan, setPlan] = useState<FleetPlan | null>(null);
  // „Zajęte do” wg planu TMS tylko z przełącznikiem (12.10.2026).
  const tmsPlanOn = useFeature(OCCUPIED_UNTIL_TMS_PLAN);
  const plan = loadedPlan && !tmsPlanOn ? fleetWithoutTmsPlan(loadedPlan) : loadedPlan;
  const [caretakers, setCaretakers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mine = useOnlyMine('fleet-schedule');
  // Włączone „Tylko moje pojazdy” wybiera opiekuna z preferencji; ręczny
  // wybór innego opiekuna przełącznik wyłącza.
  const effectiveCaretaker = mine.onlyMine && mine.caretaker !== null ? mine.caretaker : caretaker;

  // Szukajka po numerze nie pyta API przy każdym znaku.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Powrót z karty pojazdu zachowuje filtry i skalę (makieta pkt 7).
  useEffect(() => {
    if (!mockup) return;
    writeFleetView({ days, fromKey, search, companyId, caretaker, carType, busy, includeArchived, show });
  }, [mockup, days, fromKey, search, companyId, caretaker, carType, busy, includeArchived, show]);

  useEffect(() => {
    let cancelled = false;
    const filters: FleetPlanFilters = {
      from: fromKey,
      to: mockup ? fleetViewTo(fromKey, days) : shiftDayKey(fromKey, VIEW_DAYS - 1),
      q,
      companyId,
      caretaker: effectiveCaretaker,
      carType,
      busy: busy || undefined,
      // Wyłączony nie idzie wcale — API domyślnie ukrywa archiwalne.
      includeArchived: (hideArchived && includeArchived) || undefined,
      ...(mockup && show ? { show } : {}),
    };
    setLoading(true);
    setLoadError(null);
    fetchFleetPlan(filters)
      .then((p) => {
        if (cancelled) return;
        setPlan(p);
        // Lista opiekunów nie zależy od filtrów — zapamiętujemy ostatnią pełną.
        if (p.caretakers.length > 0) setCaretakers(p.caretakers);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(toErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    fromKey,
    q,
    companyId,
    effectiveCaretaker,
    carType,
    busy,
    includeArchived,
    hideArchived,
    mockup,
    days,
    show,
    reloadTick,
  ]);

  // Liczba pasujących zleceń liczy się w tle — odczyt ponawiamy, dopóki
  // trwa pierwsze przeliczenie albo odświeżanie (najwyżej kilka razy).
  const matchingStatus = plan?.matching?.status;
  useEffect(() => {
    if (!matchingOn) return undefined;
    if (matchingStatus !== 'pending' && matchingStatus !== 'refreshing') return undefined;
    if (polls >= MATCHING_POLL_LIMIT) return undefined;
    const t = setTimeout(() => {
      setPolls((n) => n + 1);
      setReloadTick((n) => n + 1);
    }, MATCHING_POLL_MS);
    return () => clearTimeout(t);
  }, [matchingOn, matchingStatus, polls, plan]);

  const view = plan ? { from: new Date(plan.from), to: new Date(plan.to) } : null;
  const withAssignment = plan?.vehicles.filter((v) => v.items.some((i) => i.kind === 'assignment')).length ?? 0;
  const now = new Date();
  const extraColumns: TimelineColumn[] = mockup
    ? [
        { key: 'release', header: 'Wolny od · gdzie', width: 128 },
        ...(matchingOn ? [{ key: 'matching', header: 'Pasujące', width: 72 }] : []),
      ]
    : [];
  const gapsOf = (v: FleetPlanRow): TimelineGap[] =>
    (v.gaps ?? []).map((g) => {
      const label = v.registrationNumber ?? v.name ?? `#${v.id}`;
      return {
        key: `${v.id}-${g.from}`,
        from: g.from,
        to: g.to,
        label: gapLabel(g, matchingOn),
        title: gapTitle(g, matchingOn, plan?.matching?.horizonDays),
        ...(matchingOn
          ? { onClick: () => setMatchesFor({ carId: v.id, label, from: g.from, until: g.until }) }
          : { link: `/cars/${v.id}/show` }),
      };
    });
  const step = mockup ? fleetStep(days) : STEP_DAYS;
  const stepLabel = step === 7 ? 'tydzień' : 'dzień';

  return (
    <Card sx={{ mt: 1 }}>
      <Title title={menuLabel('fleet-schedule')} />
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <ButtonGroup size="small" variant="outlined" disabled={loading}>
            <Button onClick={() => setFromKey(shiftDayKey(fromKey, -step))}>‹ {stepLabel}</Button>
            <Button
              onClick={() =>
                setFromKey(mockup ? fleetHomeStart(todayKey(), days) : shiftDayKey(todayKey(), -DAYS_BACK))
              }
            >
              dziś
            </Button>
            <Button onClick={() => setFromKey(shiftDayKey(fromKey, step))}>{stepLabel} ›</Button>
          </ButtonGroup>
          {mockup ? (
            <ButtonGroup size="small" variant="outlined" disabled={loading} aria-label="Skala">
              {FLEET_SCALES.map((s) => (
                <Button
                  key={s}
                  variant={s === days ? 'contained' : 'outlined'}
                  aria-pressed={s === days}
                  onClick={() => {
                    setDays(s);
                    setFromKey(fleetHomeStart(todayKey(), s));
                  }}
                >
                  {scaleLabel(s)}
                </Button>
              ))}
            </ButtonGroup>
          ) : null}
          <TextField
            size="small"
            label="Numer pojazdu"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth={false}
            sx={{ width: 160 }}
          />
          <FormControl size="small" fullWidth={false} sx={{ minWidth: 150 }}>
            <InputLabel id="fleet-company">Spółka</InputLabel>
            <Select
              labelId="fleet-company"
              label="Spółka"
              value={companyId}
              onChange={(e) => setCompanyId(String(e.target.value))}
            >
              <MenuItem value="">wszystkie</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth={false} sx={{ minWidth: 170 }}>
            <InputLabel id="fleet-caretaker">Opiekun FTL</InputLabel>
            <Select
              labelId="fleet-caretaker"
              label="Opiekun FTL"
              value={effectiveCaretaker}
              onChange={(e) => {
                const value = String(e.target.value);
                if (mine.onlyMine && value !== mine.caretaker) mine.setOnlyMine(false);
                setCaretaker(value);
              }}
            >
              <MenuItem value="">wszyscy</MenuItem>
              {/* Opiekun z preferencji na liście zawsze — zanim przyjdzie plan. */}
              {(effectiveCaretaker && !caretakers.includes(effectiveCaretaker)
                ? [effectiveCaretaker, ...caretakers]
                : caretakers
              ).map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <OnlyMineToggle
            caretaker={mine.caretaker}
            checked={mine.onlyMine}
            onChange={(on) => {
              mine.setOnlyMine(on);
              // Wyłączenie wraca do całej floty, nie do opiekuna z preferencji.
              if (!on) setCaretaker('');
            }}
          />
          <FormControl size="small" fullWidth={false} sx={{ minWidth: 140 }}>
            <InputLabel id="fleet-type">Typ</InputLabel>
            <Select labelId="fleet-type" label="Typ" value={carType} onChange={(e) => setCarType(String(e.target.value))}>
              <MenuItem value="">wszystkie</MenuItem>
              {VEHICLE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {vehicleTypeLabel(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth={false} sx={{ minWidth: 170 }}>
            <InputLabel id="fleet-busy">Obsadzenie</InputLabel>
            <Select
              labelId="fleet-busy"
              label="Obsadzenie"
              value={busy}
              onChange={(e) => setBusy(e.target.value as '' | 'assigned' | 'free')}
            >
              <MenuItem value="">wszystkie pojazdy</MenuItem>
              <MenuItem value="assigned">tylko obsadzone</MenuItem>
              <MenuItem value="free">tylko wolne</MenuItem>
            </Select>
          </FormControl>
          {mockup ? (
            <FormControl size="small" fullWidth={false} sx={{ minWidth: 150 }}>
              <InputLabel id="fleet-show">Pokaż</InputLabel>
              <Select
                labelId="fleet-show"
                label="Pokaż"
                value={show}
                onChange={(e) => setShow(parseFleetShow(e.target.value))}
              >
                <MenuItem value="">wszystkie pojazdy</MenuItem>
                <MenuItem value="alert">z alertem</MenuItem>
                <MenuItem value="variant">z wariantem</MenuItem>
                {stateShowOn || FLEET_STATE_SHOW_VALUES.some((v) => v === show)
                  ? FLEET_STATE_SHOW_VALUES.map((v) => (
                      <MenuItem key={v} value={v}>
                        {FLEET_STATE_SHOW_LABELS[v]}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
          ) : null}
          {hideArchived ? <ArchivedToggle checked={includeArchived} onChange={setIncludeArchived} /> : null}
          {loading ? <CircularProgress size={18} /> : null}
        </Box>
        {loadError ? <Alert severity="error">{loadError}</Alert> : null}
        {plan && view ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {fmtDay(plan.from)} – {fmtDay(plan.to)} ·{' '}
              {plural(plan.vehicles.length, 'pojazd', 'pojazdy', 'pojazdów')}, w tym{' '}
              {plural(withAssignment, 'obsadzony', 'obsadzone', 'obsadzonych')}
              {mockup ? ' · kolejność według czasu zwolnienia' : ''}
            </Typography>
            {mockup && plan.counters ? <FleetCountersBar counters={plan.counters} /> : null}
            {plan.vehicles.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Żaden pojazd nie spełnia wybranych filtrów.
              </Typography>
            ) : (
              <PlanTimeline
                window={view}
                labelWidth={fleetLabelWidth(narrow)}
                dayMinWidth={mockup ? fleetDayMinWidth(days, narrow) : 56}
                labelHeader="Pojazd"
                size="compact"
                extraColumns={extraColumns}
                overrun={mockup}
                rows={plan.vehicles.map((v) => ({
                  key: v.id,
                  label: <VehicleLabel v={v} today={todayKey()} markers={mockup} />,
                  items: v.items,
                  months: v.months,
                  splitPhases: true,
                  ...(mockup
                    ? {
                        gaps: gapsOf(v),
                        extra: [
                          <ReleaseCell key="release" v={v} now={now} />,
                          ...(matchingOn
                            ? [
                                <MatchingCell
                                  key="matching"
                                  v={v}
                                  matching={plan.matching}
                                  onOpen={() =>
                                    setMatchesFor({ carId: v.id, label: v.registrationNumber ?? v.name ?? `#${v.id}` })
                                  }
                                />,
                              ]
                            : []),
                        ],
                      }
                    : {}),
                }))}
              />
            )}
            <PlanLegend
              splitPhases
              segments={plan.vehicles.some((v) => v.items.some(hasSegments))}
              tmsPlan={plan.vehicles.some((v) => v.items.some((i) => i.tmsPlanSegment))}
              fleetMockup={mockup}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {plan.occupancyNote}
              {plan.segmentsNote ? ` ${plan.segmentsNote}` : ''}
              {plan.tmsPlanNote ? ` ${plan.tmsPlanNote}` : ''} Filtr „tylko wolne” pokazuje pojazdy bez
              obsadzenia w wybranym okresie; warianty nie są brane pod uwagę.
              {mockup
                ? ' „Wolny od · gdzie” — kiedy i gdzie pojazd zwalnia się po ostatnim obsadzeniu (ta sama reguła pozycji co rekomendacje); luka — czas bez obsadzenia od teraz.'
                : ''}
              {matchingOn && matchingNote(plan.matching) ? ` ${matchingNote(plan.matching)}` : ''}
            </Typography>
          </>
        ) : null}
        {matchingOn ? <FleetMatchesDialog target={matchesFor} onClose={() => setMatchesFor(null)} /> : null}
      </CardContent>
    </Card>
  );
};
